import { nanoid } from "nanoid";
import Banner, { BannerItems } from "../../components/banner/Banner";
import InsertCommentIcon from "@mui/icons-material/InsertComment";
import CreditScoreIcon from "@mui/icons-material/CreditScore";
import SupportAgentIcon from "@mui/icons-material/SupportAgent";
import EditSquareIcon from "@mui/icons-material/EditSquare";
import OutboundIcon from "@mui/icons-material/Outbound";

import { useCallback, useEffect, useRef, useState } from "react";
import {
  endpoint,
  nameRegex,
  phoneRegex,
  provincieItaliane,
  randomInt,
  required_error,
} from "../../services/shared";
import { ConfigLP } from "../../types";
import HorizontalSeparator from "../../components/horizontal-separetor/HorizontalSeparetor";
import { Footer } from "../../components/footer/Footer";
import style from "./LpAgente.module.css";
import { Controller, useForm } from "react-hook-form";
import {
  Autocomplete,
  Button,
  createFilterOptions,
  TextField,
} from "@mui/material";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import axios from "axios";
import { Faq } from "../../components/faq/Faq";
import MaskedImage from "../../components/masked-image/MaskedImage";
import * as React from "react";
import { doc, getDoc } from "firebase/firestore";
import { db } from "../../services/firebase";
import { useNavigate, useParams } from "react-router-dom";
import { useAuth } from "../../providers/AuthProvider";
import {apiClient} from "../../services/api";

const bannerItems: BannerItems[] = [
  {
    id: nanoid(),
    icon: <InsertCommentIcon />,
    title: "Compila il modulo",
    subTitle: `Inserisci pochi dati per farci conoscere la tua esigenza.`,
  },
  {
    id: nanoid(),
    icon: <CreditScoreIcon />,
    title: "Analisi gratuita",
    subTitle: `Valutiamo la tua richiesta per condividere la strategia più adatta a te.`,
  },
  {
    id: nanoid(),
    icon: <SupportAgentIcon />,
    title: "Parla con un consulente",
    subTitle: `Un nostro esperto ti supporterà dall'inizio alla fine.`,
  },
  {
    id: nanoid(),
    icon: <EditSquareIcon />,
    title: "Conferisci l’incarico",
    subTitle: `Ci occupiamo noi della burocrazia bancaria, fino all'erogazione.`,
  },
];
const titles = [
  `La consulenza su misura per realizzare i tuoi progetti.`,
  `Ottieni il tuo mutuo con il giusto supporto.`,
  `Cerca la giusta casa, al mutuo giusto pensiamo noi.`,
];

const contactFormSchema = z.object({
  nome: z
    .string({ required_error })
    .trim()
    .min(2, "Nome troppo corto")
    .max(50, "Nome troppo lungo")
    .regex(nameRegex, "Il nome contiene caratteri non validi"),

  cognome: z
    .string({ required_error })
    .trim()
    .min(2, "Cognome troppo corto")
    .max(50, "Cognome troppo lungo")
    .regex(nameRegex, "Il cognome contiene caratteri non validi"),
  email: z.string().email("Email non valida").optional(),
  cellulare: z
    .string({ required_error })
    .min(8, "Cellulare non valido")
    .regex(phoneRegex, "Numero di cellulare italiano non valido"),
  provincia: z.enum(provincieItaliane, {
    required_error: "Provincia obbligatoria",
    invalid_type_error: "Provincia non valida",
  }),
  privacyConsent: z.boolean().refine((val) => val, {
    message: "Devi accettare l'informativa privacy",
  }),

  trattamentoDatiConsent: z.boolean().refine((val) => val, {
    message: "Devi acconsentire al trattamento dati",
  }),
});

export type ContactFormData = z.infer<typeof contactFormSchema>;

export default function LpAgente() {
  const navigate = useNavigate();
  const [mainTitle, setMainTitle] = useState("");
  const [subTitle, setSubTitle] = useState("");
  const [loading, setLoading] = useState(true);
  const [privacyConsent, setPrivacyConsent] = useState(false);
  const [trattamentoDatiConsent, setTrattamentoDatiConsent] = useState(false);
  const [config, setConfig] = useState<ConfigLP>();
  const { freshToken } = useAuth();

  const { hash } = useParams();

  const sectionA = useRef(null);
  const cardRef = useRef(null);

  const [dialogState, setDialogState] = useState<boolean>(false);
  const [comuniOptions, setComuniOptions] = useState<string[]>([]);
  const [addressOptions, setAddressOptions] = useState<any[]>([]);

  const richiediPreventivo = useCallback((ref: any) => {
    return goTo(ref);
  }, []);
  const cardItems: { id: string; title: string; subT: string; ref: any }[] = [
    {
      id: nanoid(),
      title: "Oltre 600 agenti",
      subT: "I nostri consulenti sono attivi in tutta la penisola italiana. Puoi incontrarli in uno dei nostri Store oppure usufruire di una consulenza online.",
      ref: cardRef,
    },
    {
      id: nanoid(),
      title: "Consulenti qualificati",
      subT: "Tutti i nostri consulenti sono iscritti presso l'organismo OAM ed autorizzati ad operare.",
      ref: cardRef,
    },
    {
      id: nanoid(),
      title: "Affidati a noi",
      subT: "Un consulente dedicato ti supporterà dalla richiesta all'erogazione, in sicurezza e trasparenza.",
      ref: cardRef,
    },
  ];
  const defaultValues: Partial<ContactFormData> = {
    privacyConsent: false,
    trattamentoDatiConsent: false,
  };
  const goTo = (ref: any) =>
    ref.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  const form = useForm<ContactFormData>({
    mode: "onChange", // validazione mentre si digita
    reValidateMode: "onChange", // rivelidazione dopo errori
    resolver: zodResolver(contactFormSchema),
    defaultValues,
  });
  const {
    register,
    reset,
    setValue,
    watch,
    handleSubmit,
    control,
    formState: { errors, isSubmitting },
  } = form;

  const comuneValue = watch("provincia");
  const filterOptions = createFilterOptions<string>({
    // matchFrom: "start" → filtra solo dall’inizio
    matchFrom: "start",
    // ignora maiuscole/minuscole e accenti
    ignoreAccents: true,
    stringify: (option) => option,
  });

  useEffect(() => {
    const randomTitle = randomInt(0, 2);
    setMainTitle(titles[randomTitle]);
  }, []);

  async function getLp(hash: string) {
    const docRef = doc(db, `lp/${hash}`);
    const snap = await getDoc(docRef);

    if (!snap.exists()) {
      console.error("Errore nel recupero dati");
    }
    return {
      ...JSON.parse(snap.data()?.config),
      user_photo: snap.data()?.user_photo,
      nomeAgente: snap.data()?.displayNameAgente,
    } as ConfigLP;
  }

  useEffect(() => {
    if (hash) {
      getLp(hash)
        .then((data) => {
          console.log({ lpConfig: data, hash });
          setConfig(data);
        })
        .finally(() => setLoading(false));
    }
  }, [hash]);

  const onSubmit = handleSubmit(async (data) => {
    try {
      let res = null;
      console.log({ data });
      const freshTK = await freshToken();

      const date = new Date();
      const y = date.getFullYear();
      const m = String(date.getMonth() + 1).padStart(2, "0");
      const d = String(date.getDate()).padStart(2, "0");

      const today = `${y}-${m}-${d}`;

      const payload = {
        ...data,
        ...config,
        c__mjslq774: today,
        tipologiaRichiesta: "Cessione del Quinto",
        distributionChannel: "hub",
        c__p4ft2h15: "Cessione del Quinto",
      };

      console.log({ payload });
      res = await apiClient.post(`/v1/leads/sender-lp-agente`, payload);
      console.log({ res });
      if (res?.status === 200) {
        reset();
        navigate(`/thank-you-lp-agente?channel=organico&lp=${hash}`, {
          state: {
            nome: data.nome,
            cognome: data.cognome,
            consulente: config?.nomeAgente,
            photoURL: config?.user_photo
          },
        });
      }
    } catch (e) {
      const errorMessage = e instanceof Error ? e.message : String(e);
      console.error("Errore durante il salvataggio", errorMessage);
    }
    setDialogState(true);
  });

  return (
    <div>
      <div className={"container"} style={{marginBottom: "2em", maxWidth: "1280px"}}>
        <Banner
          img={
            "https://firebasestorage.googleapis.com/v0/b/ws-digital-3abf0.firebasestorage.app/o/2O5bfB4HA5XsREO6tYYTuWnjm8R2%2Fimg_nasso.jpeg?alt=media&token=060175de-e51b-436a-94b3-f4b78e20aafe"
          }
          title={mainTitle}
          subTitle={`Sono ${config?.nomeAgente?.toUpperCase()}, mi occupo di mediazione del credito e sono un consulente iscritto OAM al numero M325`}
          cta={"Chiedi una consulenza!"}
          ctaClick={() => richiediPreventivo(sectionA)}
          MaskedImgComponent={() => (
            <MaskedImage
              x={-100}
              y={0}
              wContainer={280}
              wImage={460}
              hImage={300}
              dPath={"dPath3"}
              wSvg={260}
              hSvg={260}
              imgURL={
                config?.user_photo
                  ? config?.user_photo
                  : "https://firebasestorage.googleapis.com/v0/b/ws-digital-3abf0.firebasestorage.app/o/consulente_img.png?alt=media&token=fb42f2cb-2e1e-437b-ba73-40dc428477a1"
              }
            ></MaskedImage>
          )}
          roundOff={false}
          revert={false}
        />
        <div className={`${style.elevationBanner} glass`}>
          <ul className={style.cardListContainer}>
            {bannerItems.map((item) => {
              return (
                <li
                  key={item.id}
                  className="flex-row gap-2"
                  style={{ alignItems: "center" }}
                >
                  <div className={style.cardItem}>{item.icon}</div>
                  <div className="flex-col">
                    <span className={`${style.lpTitle} ${style.childTitle}`}>
                      {item.title}
                    </span>
                    <span className={`${style.lpTitle} ${style.childSubTitle}`}>
                      {item.subTitle}
                    </span>
                  </div>
                </li>
              );
            })}
          </ul>
          <div className={style.ebItem} ref={sectionA}>
            <span
              style={{ marginBottom: "1em" }}
              className={`${style.lpTitle} ${style.childTitle}`}
            >
              Compila in meno di 1 minuto
            </span>
            <form onSubmit={onSubmit} className="flex-col gap-2">
              <div className={"flex-col gap-2"}>
                <Controller
                  name="nome"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      fullWidth
                      label="Nome"
                      size="small"
                      error={!!errors.nome}
                      helperText={errors.nome?.message}
                    />
                  )}
                />
                <Controller
                  name="cognome"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      fullWidth
                      label="Cognome"
                      size="small"
                      error={!!errors.cognome}
                      helperText={errors.cognome?.message}
                    />
                  )}
                />
              </div>
              <div className={"flex-col gap-2"}>
                <Controller
                  name="provincia"
                  control={control}
                  render={({ field }) => (
                    <Autocomplete
                      fullWidth
                      options={provincieItaliane}
                      value={field.value || null}
                      onChange={(_, newValue) => field.onChange(newValue ?? "")}
                      isOptionEqualToValue={(option, value) => option === value}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="Seleziona Provincia"
                          size="small"
                          error={!!errors.provincia}
                          helperText={errors.provincia?.message}
                        />
                      )}
                    />
                  )}
                />
                <Controller
                  name="cellulare"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      fullWidth
                      label="Cellulare"
                      size="small"
                      error={!!errors.cellulare}
                      helperText={errors.cellulare?.message}
                    />
                  )}
                />
                <Controller
                  name="email"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      fullWidth
                      size="small"
                      label="Email"
                      value={field.value || ""}
                      placeholder="Inserisci email"
                      error={!!(errors as any).email}
                      helperText={(errors as any).email?.message}
                    />
                  )}
                />
              </div>
              <Controller
                name="privacyConsent"
                control={control}
                defaultValue={false}
                render={({ field }) => (
                  <div className={style.box}>
                    <label style={{ fontSize: "11px" }}>
                      <input
                        type="checkbox"
                        checked={field.value}
                        onChange={(e) => field.onChange(e.target.checked)}
                      />
                      <span style={{ marginLeft: "1em" }}>
                        Ho preso visione dell'informativa sulla Protezione dei
                        Dati Personali.
                      </span>
                    </label>
                    {errors.privacyConsent && (
                      <p style={{ color: "red", fontSize: "11px" }}>
                        {errors.privacyConsent.message}
                      </p>
                    )}
                  </div>
                )}
              />
              <Controller
                name="trattamentoDatiConsent"
                control={control}
                defaultValue={false}
                render={({ field }) => (
                  <div className={style.box}>
                    <label style={{ fontSize: "11px" }}>
                      <input
                        type="checkbox"
                        checked={field.value}
                        onChange={(e) => field.onChange(e.target.checked)}
                      />
                      <span style={{ marginLeft: "1em" }}>
                        Acconsento al trattamento dei dati personali.
                      </span>
                    </label>
                    {errors.trattamentoDatiConsent && (
                      <p style={{ color: "red", fontSize: "11px" }}>
                        {errors.trattamentoDatiConsent.message}
                      </p>
                    )}
                  </div>
                )}
              />
              <Button
                variant={"contained"}
                type={"submit"}
                disabled={isSubmitting}
              >
                {isSubmitting
                  ? "Elaborazione in corso"
                  : "Chiedi una consulenza!"}
              </Button>
            </form>
          </div>
        </div>
        <div
          className={"flex-col"}
          style={{
            minHeight: "50vh",
            margin: "1.5em 0",
            justifyContent: "center",
          }}
        >
          <div className={style.sectionMessage}>
            <h1 style={{ margin: "0" }}>
              Sempre più famiglie scelgono Affida!
            </h1>
            <p className={style.lpSubTitle}>
              Nel 2025 abbiamo intermediato oltre 1 miliardo di euro, in
              richieste di accesso al credito, in tutta Italia 🇮🇹
            </p>
          </div>
          {/*<HorizontalSeparator maxWidth={"960px"} />*/}
          <div
            className={style.cardColorContainer}
            style={{ justifyContent: "center" }}
          >
            {cardItems.map((item, index) => {
              return (
                <div
                  key={item.id}
                  className={
                    style.cardColor +
                    " " +
                    (index === 0
                      ? style.coral
                      : index === 1
                        ? style.green
                        : style.blue)
                  }
                >
                  <OutboundIcon
                    className={style.cardColorIcon}
                    onClick={() => richiediPreventivo(sectionA)}
                  ></OutboundIcon>
                  <span className={style.titleCardColor}>{item.title}</span>
                  <span className={style.subTitleCardColor}>{item.subT}</span>
                </div>
              );
            })}
          </div>
        </div>
        <HorizontalSeparator maxWidth={"70%"} />
        <Faq ctaClick={() => richiediPreventivo(sectionA)}></Faq>
      </div>
      <Footer />
    </div>
  );
}
